import { API_BASE_URL } from '@/config/setting';

/** 获取服务器时间 */
export function date() {
  return new Date(dateNow());
}

/** 获取服务器时间戳（毫秒） */
export function dateNow() {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', `${API_BASE_URL}/bsl/tenant/date/now`, false); // 同步请求
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.send();

  if (xhr.status === 200) {
    try {
      const result = JSON.parse(xhr.responseText);
      if (result && result.code === 0 && typeof result.data === 'number') {
        return result.data;
      }
    } catch (err) {
      console.error('解析服务器时间失败:', err);
    }
  }
  return Date.now();
}

/** 判断当前时间戳 是否在传入的日期之前 */
export function dateNowIsBefore(date: string | number | Date) {
  if (!date) {
    throw 'dateNowIsBefore error: date is null';
  }
  return dateNow() < parseDate(date)!.getTime();
}

/** 判断当前时间戳 是否在传入的日期之后 */
export function dateNowIsAfter(date: string | number | Date) {
  if (!date) {
    throw 'dateNowIsAfter error: date is null';
  }
  return dateNow() > parseDate(date)!.getTime();
}

/** 判断当前时间戳 是否在传入的日期之内 */
export function dateNowIsBetween(
  start: string | number | Date,
  end: string | number | Date
) {
  if (!start) {
    throw 'dateNowIsBetween error: start is null';
  }
  if (!end) {
    throw 'dateNowIsBetween error: end is null';
  }
  const currDateNow = dateNow();
  return (
    currDateNow >= parseDate(start)!.getTime() &&
    currDateNow <= parseDate(end)!.getTime()
  );
}

/**
 * 日期格式化输出
 *
 * dateFormat(new Date(), 'yyyy年MM月dd日')
 *
 * @param date 日期
 * @param format 'yyyy-MM-dd', 'hh:mm:ss', 'yyyy-MM-dd hh:mm:ss', 'yyyy年MM月dd日', 'hh时mm分ss秒', 'yyyy年MM月dd日 hh时mm分ss秒'
 */
export function dateFormat(
  date: string | number | Date,
  format: string = 'yyyy-MM-dd hh:mm:ss'
): string {
  if (typeof date === 'number' || typeof date === 'string') {
    if (typeof date === 'string') {
      date = date
        .replace('年', '/')
        .replace('月', '/')
        .replace('日', '')
        .replace(/-+/g, '/')
        .replace('时', ':')
        .replace('分', ':')
        .replace('秒', '');
    }
    date = new Date(date);
  }
  const o = {
    M: date.getMonth() + 1, // 月份
    d: date.getDate(), // 日
    h: date.getHours(), // 小时
    m: date.getMinutes(), // 分
    s: date.getSeconds(), // 秒
    q: Math.floor((date.getMonth() + 3) / 3), // 季度
    S: date.getMilliseconds() // 毫秒
  };
  let dateStr = format;

  // 年月日
  dateStr = dateStr.replace(/yyyy|YYYY/, String(date.getFullYear()));
  dateStr = dateStr.replace(/MM/, String(o.M > 9 ? o.M : '0' + o.M));
  dateStr = dateStr.replace(/M/g, String(o.M));
  dateStr = dateStr.replace(/dd|DD/, String(o.d > 9 ? o.d : '0' + o.d));
  dateStr = dateStr.replace(/d|D/g, String(o.d));

  // 时分
  dateStr = dateStr.replace(/hh|HH/, String(o.h > 9 ? o.h : '0' + o.h));
  dateStr = dateStr.replace(/h|H/g, String(o.h));
  dateStr = dateStr.replace(/mm/, String(o.m > 9 ? o.m : '0' + o.m));
  dateStr = dateStr.replace(/m/g, String(o.m));

  // 秒
  dateStr = dateStr.replace(/ss/, String(o.s > 9 ? o.s : '0' + o.s));
  dateStr = dateStr.replace(/s/g, String(o.s));

  // 毫秒
  dateStr = dateStr.replace(
    /SSS/,
    String(o.S > 99 ? o.S : o.S > 9 ? '0' + o.S : '00' + o.S)
  );
  dateStr = dateStr.replace(/SS/, String(o.S > 9 ? o.S : '0' + o.S));
  dateStr = dateStr.replace(/S/g, String(o.S));

  return dateStr;
}

/** 日期转换 */
export function parseDate(date?: string | number | Date): Date | null {
  if (!date) {
    return null;
  }
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'number') {
    return new Date(date);
  }
  if (typeof date !== 'string') {
    return null;
  }
  date = date
    .replace('年', '/')
    .replace('月', '/')
    .replace('日', '')
    .replace(/-+/g, '/')
    .replace('时', ':')
    .replace('分', ':')
    .replace('秒', '')
    .trim();
  if (date.length === 10) {
    date += ' 00:00:00';
  }
  try {
    return new Date(date);
  } catch (err) {
    console.error(err);
    return null;
  }
}

/** 获取时间戳 */
export function getDatetime(date: string | number | Date): number | null {
  const value = parseDate(date);
  return value ? value.getTime() : null;
}

/** 本周时间范围 */
export function nowWeekRange(curr?: Date) {
  const today = curr ?? date();
  const day = today.getDay(); // 0=周日, 1=周一, ... 6=周六

  // 计算本周一（ISO标准：周一为第一天）
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));

  // 计算本周日（周一 + 6天）
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return {
    start: new Date(monday.setHours(0, 0, 0, 0)), // 本周一 00:00:00
    end: new Date(sunday.setHours(23, 59, 59, 999)) // 本周日 23:59:59
  };
}

/** 本月时间范围 */
export function nowMonthRange(curr?: Date) {
  const now = curr ?? date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0=1月, 1=2月, ... 11=12月

  // 本月第一天：当前年月1号
  const firstDay = new Date(currentYear, currentMonth, 1);

  // 本月最后一天：下个月1号的前一天（关键技巧！）
  const lastDay = new Date(currentYear, currentMonth + 1, 0);

  return {
    start: new Date(firstDay.setHours(0, 0, 0, 0)), // 例如：2025-11-01
    end: new Date(lastDay.setHours(23, 59, 59, 999)) // 例如：2025-11-30
  };
}
